import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  formControlLabelClasses,
  Grid2 as Grid,
  inputBaseClasses,
  TextField,
  Tooltip,
  typographyClasses,
} from '@mui/material'

import { ThemeConfig } from 'src/common/config'
import { ELEMENT_HEIGHT } from 'src/common/constants'
import { Entity, useAddEntity, useFetchOne } from 'src/common/hooks'
import type {
  IBuyersCard,
  IInkoperPlaat,
  Vestiging,
} from 'src/common/services/client'
import SnackbarUtils from 'src/common/utils/SnackbarUtils'

import { useCardRowsStore, usePurchaserStore } from '../stores'
import BuyerCardWarning from './BuyerCardWarning'

type AddPurchaserCardDialogProps = {
  onClose: () => void
  onCancel: () => void
}

export function AddPurchaserCardDialog({
  onClose: close,
  onCancel: cancel,
}: Readonly<AddPurchaserCardDialogProps>): JSX.Element {
  const { t } = useTranslation()
  const addEntity = useAddEntity<IInkoperPlaat>(Entity.IInkoperPlaat)
  const {
    userId,
    purchaser,
    setMustRefetchPurchaser,
    setMustRefetchPurchaserCards,
  } = usePurchaserStore()

  const [searchStringPlate, setSearchStringPlate] = useState(0)
  const [filter, setFilter] = useState<number>(undefined)
  const [newCards, setNewCards] = useState<IInkoperPlaat[]>([])
  const [buyersCard, setBuyersCard] = useState<IBuyersCard>(undefined)
  const { cardRows } = useCardRowsStore()
  const { data: fetchedBuyerCard } = useFetchOne<IBuyersCard>(
    {
      key: filter,
    },
    Entity.BuyersCard,
    open && !!searchStringPlate
  )

  const usedLocationsForPlate = useMemo(() => {
    if (!buyersCard?.buyersCardId || !cardRows) return []

    const vestigingen = ['aalsmeer', 'naaldwijk', 'rijnsburg', 'eelde'] as const

    return vestigingen.filter(vestiging =>
      cardRows.some(
        row =>
          row.koperPlaatNummer === buyersCard.buyersCardId &&
          row[vestiging] === true
      )
    )
  }, [buyersCard, cardRows])

  const handleSubmit = async () => {
    if (addEntity.isLoading) return Promise.resolve()

    if (newCards.length === 0) {
      resetState()
      close()
      return Promise.resolve()
    }

    try {
      const promises: Promise<IInkoperPlaat>[] = []
      const temp = [
        ...newCards.filter(
          (obj, index) =>
            newCards.findIndex(
              item =>
                item.koperPlaatNummer === obj.koperPlaatNummer &&
                item.inkoperIdentificatie === obj.inkoperIdentificatie &&
                item.vestigingCode === obj.vestigingCode
            ) === index
        ),
      ]
      temp.forEach(card => {
        const promise = addEntity.mutateAsync(card)
        promises.push(promise)
      })
      await Promise.all(promises)
      resetState()
      close()
    } catch (error) {
      SnackbarUtils.error(`${error}`)
    }
  }

  const handleCancel = useCallback(() => {
    resetState()
    cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetState = () => {
    setMustRefetchPurchaser(true)
    setMustRefetchPurchaserCards(true)
    setNewCards([])
    setSearchStringPlate(0)
    setFilter(undefined)
  }

  const handleFilterBuyerNumber = (
    evt: ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchStringPlate(Number(evt.target.value))
  }

  const handleUserKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      setFilter(searchStringPlate)
    }
  }

  const handleSearchButtonClick = (event: MouseEvent) => {
    event.preventDefault()
    setFilter(searchStringPlate)
  }

  const handleCardChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (searchStringPlate === 0) return
    if (target.checked) {
      const newCard: IInkoperPlaat = {
        inkoperIdentificatie: purchaser.inkoperIdentificatie,
        koperPlaatNummer: buyersCard.buyersCardId,
        mutatieDatumTijd: new Date(),
        mutatieGebruiker: userId,
        vestigingCode: target.name as Vestiging,
      }
      setNewCards([...newCards, newCard])
    } else {
      setNewCards(
        newCards.filter(c => c.vestigingCode !== (target.name as Vestiging))
      )
    }
  }

  const isBuyerCardOfAnotherClient = useMemo(
    () =>
      fetchedBuyerCard?.buyersCardId > 0 &&
      fetchedBuyerCard?.customerRfhRelationId > 0 &&
      fetchedBuyerCard.buyersCardId !== fetchedBuyerCard.customerRfhRelationId,
    [fetchedBuyerCard?.buyersCardId, fetchedBuyerCard?.customerRfhRelationId]
  )

  useEffect(() => {
    if (fetchedBuyerCard) {
      setBuyersCard(fetchedBuyerCard)
    }
    return () => {
      setBuyersCard(undefined)
      setSearchStringPlate(undefined)
    }
  }, [fetchedBuyerCard, setSearchStringPlate])

  return (
    <Dialog
      open
      onClose={handleCancel}
      maxWidth='md'
      PaperProps={{ sx: { minWidth: '450px' } }}
    >
      <DialogTitle
        sx={{
          [`&.${typographyClasses.root}`]: {
            fontSize: '1.3rem',
            lineHeight: '1.75rem',
          },
        }}
      >
        {t('purchaser.purchaserCard.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box
          component={'form'}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            boxSizing: 'content-box',
            marginTop: ThemeConfig.spacing.s,
            marginRight: ThemeConfig.spacing.xs * 0.5,
          }}
        >
          <TextField
            id='koperplaatnummer'
            name='koperplaatnummer'
            fullWidth
            placeholder={t('buyersCards.plateNumber')}
            onChange={handleFilterBuyerNumber}
            onKeyDown={handleUserKeyPress}
            sx={{
              [`& .${inputBaseClasses.input}`]: {
                marginLeft: ThemeConfig.spacing.s,
              },
              height: ELEMENT_HEIGHT,
            }}
          />
          <Button
            variant='contained'
            onClick={handleSearchButtonClick}
            sx={{
              boxSizing: 'border-box',
              height: ELEMENT_HEIGHT,
              marginLeft: '0.5rem',
            }}
          >
            {t('common.search')}
          </Button>
        </Box>
        {isBuyerCardOfAnotherClient && (
          <BuyerCardWarning
            buyerNumber={purchaser?.koperNummer}
            otherBuyerNumber={fetchedBuyerCard?.customerRfhRelationId}
          />
        )}
        <Grid
          container
          my={2}
          spacing={2}
          sx={{ minHeight: '200px' }}
          direction={'column'}
        >
          {buyersCard?.aalsmeer && (
            <Tooltip
              title={
                usedLocationsForPlate.includes('aalsmeer')
                  ? t('purchaser.purchaserCard.addDialog.isInTheList')
                  : ''
              }
              arrow
              disableHoverListener={!usedLocationsForPlate.includes('aalsmeer')}
            >
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='aalsmeer'
                      onChange={handleCardChange}
                      disabled={usedLocationsForPlate.includes('aalsmeer')}
                    />
                  }
                  label='Aalsmeer'
                  sx={{
                    [`& .${formControlLabelClasses.label}`]: {
                      fontWeight: 100,
                    },
                  }}
                />
              </span>
            </Tooltip>
          )}

          {buyersCard?.naaldwijk && (
            <Tooltip
              title={
                usedLocationsForPlate.includes('naaldwijk')
                  ? t('purchaser.purchaserCard.addDialog.isInTheList')
                  : ''
              }
              arrow
              disableHoverListener={
                !usedLocationsForPlate.includes('naaldwijk')
              }
            >
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='naaldwijk'
                      onChange={handleCardChange}
                      disabled={usedLocationsForPlate.includes('naaldwijk')}
                    />
                  }
                  label='Naaldwijk'
                  sx={{
                    [`& .${formControlLabelClasses.label}`]: {
                      fontWeight: 100,
                    },
                  }}
                />
              </span>
            </Tooltip>
          )}

          {buyersCard?.rijnsburg && (
            <Tooltip
              title={
                usedLocationsForPlate.includes('rijnsburg')
                  ? t('purchaser.purchaserCard.addDialog.isInTheList')
                  : ''
              }
              arrow
              disableHoverListener={
                !usedLocationsForPlate.includes('rijnsburg')
              }
            >
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='rijnsburg'
                      onChange={handleCardChange}
                      disabled={usedLocationsForPlate.includes('rijnsburg')}
                    />
                  }
                  label='Rijnsburg'
                  sx={{
                    [`& .${formControlLabelClasses.label}`]: {
                      fontWeight: 100,
                    },
                  }}
                />
              </span>
            </Tooltip>
          )}

          {buyersCard?.eelde && (
            <Tooltip
              title={
                usedLocationsForPlate.includes('eelde')
                  ? t('purchaser.purchaserCard.addDialog.isInTheList')
                  : ''
              }
              arrow
              disableHoverListener={!usedLocationsForPlate.includes('eelde')}
            >
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      name='eelde'
                      onChange={handleCardChange}
                      disabled={usedLocationsForPlate.includes('eelde')}
                    />
                  }
                  label='Eelde'
                  sx={{
                    [`& .${formControlLabelClasses.label}`]: {
                      fontWeight: 100,
                    },
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Grid>
        <DialogActions
          sx={{
            width: '100%',
            marginX: 0,
            flex: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button variant='outlined' onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant='contained' onClick={handleSubmit}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
